<template>
  <el-form
      @submit.prevent="validateSubmit('employeeForm')"
      ref="employeeForm"
      :model="employee"
      :rules="rules"
      label-position="top"
      novalidate
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
              :title="$t('actions.saveChanges')"
              isSuccess
              faIcon="save"
              @click.prevent="validateSubmit('employeeForm')"
          />
        </div>

        <div class="md:w-4/12 m-2" v-if="enableClearForm">
          <horizontal-button
              :title="$t('actions.clearButton')"
              isDanger
              faIcon="trash"
              @click.prevent="clearForm('employeeForm')"
          />
        </div>
      </div>
    </input-section>

    <!-- @submit errors -->
    <alert-badge
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section>
      <h1>{{ t('sections.generalInfo') }}</h1>

      <input-group>
        <el-form-item :label="t('name')" prop="name" class="input-label">
          <el-input
            id="name"
            v-model="employee.name"
            :placeholder="t('placeholders.name')"
            maxlength="255"
            show-word-limit
            clearable
            required
          >
          </el-input>
        </el-form-item>

        <el-form-item :label="t('gender')" prop="gender" class="input-label">
          <el-select
            id="gender"
            v-model="employee.gender"
            class="w-full"
            :placeholder="t('placeholders.gender')"
            clearable
          >
            <el-option value="male" :label="t('genderMale')" />
            <el-option value="female" :label="t('genderFemale')" />
          </el-select>
        </el-form-item>

        <div class="flex-shrink-0 mt-[45px]">
          <ul class="flex flex-wrap el-upload-list el-upload-list--picture-card">
            <li v-if="avatarSrc || pendingAvatarUrl" class="el-upload-list__item">
              <div class="w-[148px]">
                <img class="el-upload-list__item-thumbnail" :src="avatarSrc || pendingAvatarUrl" alt="" />
                <span class="el-upload-list__item-actions">
                  <span class="el-upload-list__item-delete">
                    <el-icon @click="employee.id ? removeAvatar() : removePendingAvatar()"><Delete /></el-icon>
                  </span>
                </span>
              </div>
            </li>
            <li v-if="!avatarSrc && !pendingAvatarUrl">
              <el-upload
                class="w-[146px] h-[147px]"
                drag
                :show-file-list="false"
                :action="`${User.ENDPOINT}/${employee.id}/avatar`"
                :http-request="employee.id ? handleAvatarUpload : handlePendingAvatar"
                :before-upload="beforeAvatarUpload"
                accept="image/*"
              >
                <div class="m-[17px] text-xl">
                  <el-icon><Plus /></el-icon>
                </div>
              </el-upload>
            </li>
          </ul>
        </div>
      </input-group>

      <input-group>
        <el-form-item :label="t('phone')" prop="phone" class="input-label">
          <el-input
              id="phone"
              v-model="employee.phone"
              :placeholder="t('placeholders.phone')"
              maxlength="15"
              dir="ltr"
          >
          </el-input>
        </el-form-item>

        <el-form-item :label="t('email')" prop="email" class="input-label">
          <el-input
              id="email"
              v-model="employee.email"
              type="email"
              :placeholder="t('placeholders.email')"
              dir="ltr"
              maxlength="255"
          >
          </el-input>
        </el-form-item>
      </input-group>

      <el-form-item :label="t('streetAddress')" prop="streetAddress" class="input-label">
        <el-input
            id="streetAddress"
            v-model="employee.streetAddress"
            :placeholder="t('placeholders.streetAddress')"
            show-word-limit
            maxlength="255"
            clearable
        >
        </el-input>
      </el-form-item>

      <input-group>
        <el-form-item :label="t('city')" prop="city" class="input-label">
          <el-input
              id="city"
              v-model="employee.city"
              :placeholder="t('placeholders.city')"
              maxlength="70"
              show-word-limit
              clearable
          >
          </el-input>
        </el-form-item>

        <el-form-item :label="t('postalCode')" prop="postalCode" class="input-label">
          <el-input
              id="postalCode"
              v-model="employee.postalCode"
              :placeholder="t('placeholders.postalCode')"
              show-word-limit
              maxlength="10"
              clearable
          >
          </el-input>
        </el-form-item>

        <el-form-item :label="t('country')" prop="country" class="input-label">
          <el-input
              id="country"
              :placeholder="t('placeholders.country')"
              maxlength="70"
              show-word-limit
              clearable
              v-model="employee.country"
          >
          </el-input>
        </el-form-item>
      </input-group>
    </input-section>

    <input-section>
      <h1>{{ t('sections.employmentInfo') }}</h1>

      <input-group>
        <el-form-item :label="t('employeeTitle')" prop="title" class="input-label">
          <el-input
              id="title"
              v-model="employee.employeeTitle"
              :placeholder="t('placeholders.title')"
              maxlength="127"
              show-word-limit
              clearable
          >
          </el-input>
        </el-form-item>

        <el-form-item label="&#8205;" prop="isOwner" class="input-label">
          <el-checkbox
              id="isOwner"
              v-model="employee.isOwner"
              :label="t('isOwner')"
          />
        </el-form-item>
      </input-group>

      <el-form-item :label="t('tags')" prop="extraProperties.tags" class="input-label">
        <el-select
          id="tags"
          v-model="employee.extraProperties.tags"
          class="w-full"
          multiple
          filterable
          allow-create
          default-first-option
          :placeholder="t('placeholders.tags')"
        >
          <el-option
            v-for="tag in systemTags"
            :key="tag"
            :label="tag"
            :value="tag"
          ></el-option>
        </el-select>
        <small class="help-text">
          {{ t('tagsHelpText') }}
        </small>
      </el-form-item>

      <el-form-item :label="t('employeeSalaryAmount')" prop="salaryAmount" class="input-label">
        <money-input
            id="salary-amount"
            :currency="$ctx.currency"
            v-model="employee.employeeSalaryAmount"
            @dinero-created="(dinero) => (employee.salaryDinero = dinero)"
        />
      </el-form-item>

      <input-group>
        <el-form-item :label="t('employeeStartDate')" prop="employeeStartDate" class="input-label">
          <el-date-picker
              id="start-date"
              v-model="employee.employeeStartDate"
              type="date"
              :placeholder="t('employeeStartDate')"
          >
          </el-date-picker>
        </el-form-item>

        <el-form-item :label="t('employeeEndDate')" prop="employeeEndDate" class="input-label">
          <el-date-picker
              id="end-date"
              v-model="employee.employeeEndDate"
              type="date"
              :placeholder="t('employeeEndDate')"
          >
          </el-date-picker>
        </el-form-item>
      </input-group>
    </input-section>

    <input-section>
      <h1>{{ t('sections.attachments') }}</h1>
      <EmployeeAttachmentsManager :data="employee" v-model="pendingAttachments" />
    </input-section>

    <input-section>
      <h1>{{ t('sections.loginInfo') }}</h1>
      <el-form-item :label="t('username')" prop="username" class="input-label">
        <el-input
            id="username"
            :placeholder="t('placeholders.username')"
            maxlength="255"
            show-word-limit
            clearable
            v-model="employee.username"
        >
        </el-input>
      </el-form-item>
      <el-form-item :label="t('password')" prop="newPassword" class="input-label">
        <el-input
            id="newPassword"
            :placeholder="t('placeholders.password')"
            maxlength="255"
            show-word-limit
            clearable
            v-model="employee.newPassword"
        >
        </el-input>
      </el-form-item>
      <el-form-item :label="t('pin')" prop="newPin" class="input-label">
        <el-input
            id="newPin"
            :placeholder="t('placeholders.pin')"
            maxlength="4"
            show-word-limit
            clearable
            v-model="employee.newPin"
        >
        </el-input>
      </el-form-item>

      <el-form-item :label="t('role')" prop="role" class="input-label">
        <el-select
          id="role"
          v-model="employee.roleID"
          :trigger-on-focus="false"
          value-key="name"
          class="w-full"
          filterable
          remote
        >
          <el-option
            v-for="option in availableRoles"
            :key="option.id"
            :label="$ctx.locale === 'ar' ? option.arName : option.enName"
            :value="option.id"
          ></el-option>
        </el-select>
      </el-form-item>

      <el-form-item :label="t('locations')" prop="extraProperties.locationIDs" class="input-label">
        <el-select
          id="locations"
          v-model="employee.extraProperties.locationIDs"
          :trigger-on-focus="false"
          value-key="id"
          class="w-full"
          multiple
          filterable
          :disabled="!canChangeLocations"
          :placeholder="t('placeholders.locations')"
        >
          <el-option
            v-for="option in availableLocations"
            :key="option.id"
            :label="$ctx.locale === 'ar' ? option.arName : option.enName"
            :value="option.id"
            :disabled="!canChangeLocations && option.id !== $ctx.currentLocation.id"
          ></el-option>
        </el-select>
      </el-form-item>
    </input-section>

    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
              :title="$t('actions.saveChanges')"
              isSuccess
              faIcon="save"
              @click.prevent="validateSubmit('employeeForm')"
          />
        </div>

        <div class="md:w-4/12 m-2" v-if="enableClearForm">
          <horizontal-button
              :title="$t('actions.clearButton')"
              isDanger
              faIcon="trash"
              @click.prevent="clearForm('employeeForm')"
          />
        </div>
      </div>
    </input-section>
  </el-form>
</template>

<script lang="ts">
import {defineComponent, nextTick, PropType, ref, computed} from 'vue'

// components
import AlertBadge from '@/components/AlertBadge.vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import InputSection from '@/components/form/InputSection.vue'
import InputGroup from '@/components/form/InputGroup.vue'
import {ElForm, ElMessage} from 'element-plus'
import type {UploadRequestOptions} from 'element-plus'
import {Delete, Plus} from '@element-plus/icons-vue'

// models
import User, {userI18nMessages, UserPermission} from '@/models/user/User'
import Location from '@/models/company/Location'
import {useI18n} from 'vue-i18n'
import {useHTTP} from '@/plugins/http'
import {useSettingsStore} from '@/stores/settingsStore'
import MoneyInput from '@/components/form/MoneyInput.vue'
import EmployeeAttachmentsManager from '@/components/file-manager/EmployeeAttachmentsManager.vue'

// data
import {
  cashierPermissions,
  defaultAllFalse,
  managerPermissions,
  ownerPermissions,
  PermGroup
} from './predefinedPermissionGroups'
import PermissionGroup from '@/models/user/Permission'
import { Role } from '@/models/user/Role'
import { ErrorResponse } from '@/types/types'

export default defineComponent({
  name: 'employee-form',

  setup() {
    const {t} = useI18n({
      useScope: 'global',
      messages: {
        en: {
          ...userI18nMessages.en,
          password: 'Password',
          pin: 'Cashier Pin',

          sections: {
            generalInfo: 'General Information',
            employmentInfo: 'Employment Information',
            attachments: 'Attachments',
            loginInfo: 'Login Information',
            permissions: 'Permissions'
          },

          placeholders: {
            name: 'Example: Ahmed',
            gender: 'Select gender',
            phone: 'Example: 077011111111',
            email: 'Example: ahmed@gmail.com',
            password: '123',
            pin: '4432',
            streetAddress: 'Example: Next to Almansour Mall',
            city: 'Example: Baghdad',
            postalCode: 'Example: 10015',
            country: 'Example: Iraq',
            title: 'Example: Sales Rep',
            salary: 'Example: IQD 1,000,000',
            username: 'Example: ahmedjoubouri',
            locations: 'Select locations',
            tags: 'Select or type tags'
          },

          avatarAfterSave: 'Save first to upload avatar',
          avatarUploadSuccess: 'Avatar uploaded',
          avatarRemoveSuccess: 'Avatar removed',
          avatarFileTooLarge: 'Image must be less than 2MB',

          tags: 'Employee Tags',
          tagsHelpText: 'Tags help categorize employees. Common tags: Sales Rep, Optometrist, Money Collector',

          permGroup: 'Permission Group',

          actions: {
            create: 'Create',
            read: 'Read',
            update: 'Update',
            delete: 'Delete'
          },
          role: 'Role',
          locations: 'Locations',
          casher: 'Casher',
          optometrist: 'Optometrist',
          other: 'Other'
        },
        ar: {
          ...userI18nMessages.ar,
          password: 'رمز الدخول',
          pin: 'رمز الكاشير',

          sections: {
            generalInfo: 'المعلومات العامة',
            employmentInfo: 'المعلومات الوظيفية',
            attachments: 'المرفقات',
            loginInfo: 'تسجيل الدخول',
            permissions: 'تحديد الصلاحيات'
          },

          placeholders: {
            name: 'مثلا: احمد',
            gender: 'اختر الجنس',
            phone: 'مثلا: 077011111111',
            email: 'مثلا: ahmed@gmail.com',
            password: 'Password123',
            pin: '4432',
            streetAddress: 'مثلا: مجاور لمول المنصور',
            city: 'مثلا: بغداد',
            postalCode: 'مثلا: 10015',
            country: 'مثلا: العراق',
            title: 'مثلا: موظف مبيعات',
            salary: 'مثلا: 1,000,000',
            username: 'مثلا: ahmedjoubouri',
            locations: 'اختر المواقع',
            tags: 'اختر أو اكتب التصنيفات'
          },

          avatarAfterSave: 'احفظ اولاً لرفع الصورة',
          avatarUploadSuccess: 'تم رفع الصورة',
          avatarRemoveSuccess: 'تم حذف الصورة',
          avatarFileTooLarge: 'يجب ان يكون حجم الصورة اقل من 2 ميغابايت',

          tags: 'الوسوم',
          tagsHelpText: 'التصنيفات تساعد في تصنيف الموظفين. تصنيفات شائعة: موظف مبيعات، طبيب عيون، محصل نقود',

          permGroup: 'الصلاحية',

          actions: {
            create: 'انشاء',
            read: 'قراءة',
            update: 'تحديث',
            delete: 'مسح'
          },
          role: 'الصلاحيات',
          locations: 'المواقع',
          casher: 'كاشير',
          optometrist: 'طبيب الفحص',
          other: 'اخرى'
        }
      }
    })

    const rules = ref({
      name: [
        {required: true, message: t('validation.required')},
        {max: 127, message: t('validation.maxLength', {len: 127})}
      ],
      phone: [{max: 15, message: t('validation.maxLength', {len: 15})}],
      email: [{max: 255, message: t('validation.maxLength', {len: 255})}],
      streetAddress: [{max: 255, message: t('validation.maxLength', {len: 255})}],
      city: [{max: 70, message: t('validation.maxLength', {len: 70})}],
      postalCode: [{max: 10, message: t('validation.maxLength', {len: 10})}],
      country: [{max: 70, message: t('validation.maxLength', {len: 70})}],
      newPassword: [
      ],
      username: [
        {max: 255, message: t('validation.maxLength', {len: 255})}
      ],
      newPin: [
        {max: 4, message: t('validation.maxLength', {len: 4})}
      ],
      role: [
      ]
    })

    const predefinedPermissionGroups = [
      {
        id: 'allFalse',
        value: defaultAllFalse
      },
      {
        id: 'owner',
        value: ownerPermissions
      },
      {
        id: 'manager',
        value: managerPermissions
      },
      {
        id: 'cashier',
        value: cashierPermissions
      }
    ]

    const permissionDropdownValue = ref('')

    const systemTags = ref([
      'Sales Rep',
      'Optometrist',
      'Money Collector',
      'Vet'
    ])

    const http = useHTTP()
    const settingsStore = useSettingsStore()

    return {
      t,
      rules,
      predefinedPermissionGroups,
      permissionDropdownValue,
      systemTags,
      http,
      settingsStore,
      User
    }
  },

  components: {
    MoneyInput,
    AlertBadge,
    HorizontalButton,
    InputSection,
    InputGroup,
    EmployeeAttachmentsManager,
    Delete,
    Plus
  },
  props: {
    initEmployee: {
      type: User,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    enableClearForm: {
      type: Boolean,
      required: false
    },
    clearFormOnSubmit: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      employee: this.initEmployee,
      prmGroups: this.permissionGroups,
      error: { title: '', body: '' },
      availableRoles: new Array<Role>(),
      availableLocations: new Array<Location>(),
      canChangeLocations: false,
      pendingAvatarFile: null as File | null,
      pendingAvatarUrl: '',
      pendingAttachments: [] as File[]
    }
  },

  async beforeMount() {
    try {
      const resp = await this.$http.get<Role[]>('/user/roles')
      this.availableRoles = resp
    } catch (errorResp) {
      const error = errorResp as ErrorResponse
      this.error.title = error.title
      this.error.body = error.body || ''
    }

    try {
      // Check if user can update /employees/locations
      this.canChangeLocations = this.$can('create', '/employees/locations')

      // Always load all locations so they can see them
      const resp = await this.$http.get<Location[]>('/company/locations')
      this.availableLocations = resp

      // If user cannot change locations, set to current location only
      if (!this.canChangeLocations && this.$ctx.currentLocation && this.$ctx.currentLocation.id) {
        // Set employee location to current location if not already set
        if (!this.employee.extraProperties.locationIDs || this.employee.extraProperties.locationIDs.length === 0) {
          this.employee.extraProperties.locationIDs = [this.$ctx.currentLocation.id]
        }
      }
    } catch (errorResp) {
      const error = errorResp as ErrorResponse
      this.error.title = error.title
      this.error.body = error.body || ''
    }
  },

  computed: {
    avatarSrc(): string {
      if (this.employee.avatar) {
        return this.settingsStore.storagePath + '/' + this.employee.avatar
      }
      return ''
    }
  },

  methods: {
    makeTitle({row}: { row: { groupName: string } }) {
      return row?.groupName ? [1, 5] : [1, 1]
    },

    beforeAvatarUpload(file: File) {
      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isLt2M) {
        ElMessage.error(this.t('avatarFileTooLarge'))
      }
      return isLt2M
    },

    async handleAvatarUpload(options: UploadRequestOptions) {
      const data = new FormData()
      data.set('avatar', options.file)
      try {
        await this.http.post(`${User.ENDPOINT}/${this.employee.id}/avatar`, data)
        await this.refreshEmployee()
        ElMessage.success(this.t('avatarUploadSuccess'))
      } catch (e: any) {
        ElMessage.error(e?.title || 'Upload failed')
      }
    },

    handlePendingAvatar(options: UploadRequestOptions) {
      this.pendingAvatarFile = options.file
      this.pendingAvatarUrl = URL.createObjectURL(options.file)
    },

    removePendingAvatar() {
      if (this.pendingAvatarUrl) {
        URL.revokeObjectURL(this.pendingAvatarUrl)
      }
      this.pendingAvatarFile = null
      this.pendingAvatarUrl = ''
    },

    async removeAvatar() {
      try {
        await this.http.delete(`${User.ENDPOINT}/${this.employee.id}/avatar`)
        await this.refreshEmployee()
        ElMessage.success(this.t('avatarRemoveSuccess'))
      } catch (e: any) {
        ElMessage.error(e?.title || 'Delete failed')
      }
    },

    async refreshEmployee() {
      const resp = await this.http.get<User>(`${User.ENDPOINT}/${this.employee.id}`)
      const updated = User.from(resp)
      this.employee.avatar = updated.avatar
    },

    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form.resetFields()
    },

    async validateSubmit(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form?.validate(async (valid: boolean) => {
        if (!valid) {
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        }

        if (this.employee.newPin == "") {
          this.employee.newPin = null;
        }

        const resp = await this.submitAction(
          this.employee,
          this.pendingAvatarFile,
          this.pendingAttachments
        )

        if (resp) {
          this.error.title = resp?.title
          this.error.body = resp?.body
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        } else {
          if (this.clearFormOnSubmit) {
            this.clearForm(formName)
            this.pendingAttachments = []
            this.removePendingAvatar()
          }

          this.$alertModal.showSuccess({title: this.successMsg, body: ''})
        }
        return true
      })
    }
  }
})
</script>

<style scoped>
form > h1 {
  @apply text-lg py-3 font-bold;
}

form > h2 {
  @apply py-3 font-bold;
}

formt > h3 {
  @apply py-3 font-bold;
}

form {
  @apply flex flex-col items-center;
}

.help-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.permissions-container {
  background-color: #fff;
  overflow: hidden;
}

.permissions-header {
  font-size: 14px;
  font-weight: 800;
  display: grid;
  color: #606266;
  grid-template-columns: 240px 1fr 1fr 1fr 1fr;
}

.permissions-header > div {
  padding: 12px;
  border: 1px solid #dddddd;
}

.permissions-header > div:nth-child(n + 2) {
  display: flex;
  align-items: center;
  justify-content: center;
}

.permissions-group > .group-title {
  padding: 12px;
  font-size: 14px;
  font-weight: 700;
  color: #606266;

  border: 1px solid #dddddd;
}

.permissions-group > .group-routes > .group-route {
  display: grid;
  grid-template-columns: 240px 1fr;
}

.permissions-group > .group-routes > .group-route:hover {
  background-color: #f7f7f7bb;
}

.permissions-group > .group-routes > .group-route > .route-title {
  padding: 12px;
  font-size: 14px;
  font-weight: 700;
  color: #606266;

  border: 1px solid #dddddd;
  display: flex;
  align-items: center;
}

.permissions-group > .group-routes > .group-route > .route-actions {
  display: grid;
  grid-template-columns: 25% 25% 25% 25%;
}

.permissions-group > .group-routes > .group-route > .route-actions > .route-action {
  padding: 12px;

  border: 1px solid #dddddd;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
